import * as Sentry from "@sentry/react"
import { useMutation } from "@tanstack/react-query"
import { Loader2Icon, RefreshCwIcon } from "lucide-react"
import { FormEvent, useState } from "react"
import { requestNewInvitation } from "@/api/api"
import {
  AppError,
  InvalidResendTokenError,
  InvitationNotActionableError,
} from "@/api/errors"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { cn } from "@/lib/utils"

const inputClasses = cn(
  "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background",
  "placeholder:text-muted-foreground",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
  "disabled:cursor-not-allowed disabled:opacity-50",
)

const RequestNewInvitation = (props: {
  tokenId: string
  resendToken: string
  signeeEmailHint?: string
}) => {
  const [email, setEmail] = useState("")

  const mutation = useMutation({
    mutationKey: ["requestNewInvitation", props.tokenId],
    mutationFn: (vars: { email: string }) =>
      requestNewInvitation({
        token_id: props.tokenId,
        resend_token: props.resendToken,
        email: vars.email,
      }),
    onSuccess: () => {
      Sentry.addBreadcrumb({
        category: "auth.resend",
        message: "invitation_resend_success",
        level: "info",
      })
    },
    onError: (error) => {
      Sentry.addBreadcrumb({
        category: "auth.resend",
        message: "invitation_resend_error",
        level: "warning",
        data: {
          name: error instanceof Error ? error.name : "Unknown",
        },
      })
    },
  })

  if (mutation.isSuccess) {
    return (
      <p
        aria-live="polite"
        className="text-center text-sm text-muted-foreground"
      >
        Te enviamos un nuevo enlace a tu correo. Revisa tu bandeja de entrada y
        la carpeta de spam.
      </p>
    )
  }

  if (mutation.error instanceof InvitationNotActionableError) {
    return (
      <p
        aria-live="polite"
        className="text-center text-sm text-muted-foreground"
      >
        El documento ya fue procesado, no es necesario solicitar un nuevo
        enlace.
      </p>
    )
  }

  if (mutation.error instanceof InvalidResendTokenError) {
    return (
      <div aria-live="polite" className="flex flex-col items-center gap-3">
        <p className="text-center text-sm text-muted-foreground">
          {mutation.error.message}
        </p>
        <Button onClick={() => window.location.reload()}>
          <RefreshCwIcon className="mr-3" />
          <span>Recargar</span>
        </Button>
      </div>
    )
  }

  const trimmed = email.trim()
  const canSubmit = trimmed.length > 0 && !mutation.isPending

  return (
    <form
      className="flex w-full flex-col gap-3"
      onSubmit={(e: FormEvent) => {
        e.preventDefault()
        if (!canSubmit) return
        mutation.mutate({ email: trimmed })
      }}
    >
      <div className="space-y-2">
        <Label htmlFor="resend-email">
          Confirma tu correo para reenviar el enlace
        </Label>
        <input
          id="resend-email"
          type="email"
          autoComplete="email"
          required
          placeholder="tu@correo.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={mutation.isPending}
          className={inputClasses}
        />
        {props.signeeEmailHint && (
          <p className="text-xs text-muted-foreground">
            Pista: {props.signeeEmailHint}
          </p>
        )}
      </div>

      <Button type="submit" disabled={!canSubmit}>
        {mutation.isPending ? (
          <>
            <Loader2Icon className="mr-3 animate-spin" />
            <span>Enviando…</span>
          </>
        ) : (
          <span>Solicitar nuevo enlace</span>
        )}
      </Button>

      {mutation.isError && !(mutation.error instanceof AppError) && (
        <p aria-live="polite" className="text-center text-sm text-destructive">
          Ha ocurrido un error. Inténtalo de nuevo.
        </p>
      )}
    </form>
  )
}

export { RequestNewInvitation }
